import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '@/lib/firebase';

// The bookmarklet code - minified version
// Note: This is stored separately and applied via ref to bypass React's javascript: URL sanitization
const BOOKMARKLET_CODE = `javascript:(function(){'use strict';var API_BASE='https://stilllovefilm.uk';var SYNC_PAGE=API_BASE+'/sync';if(!window.location.hostname.includes('letterboxd.com')){alert('Still Love Film Sync\\n\\nPlease navigate to your Letterboxd watchlist page first!\\n\\nExample: letterboxd.com/yourusername/watchlist/');return;}var path=window.location.pathname;if(!path.includes('/watchlist')&&!path.includes('/list/')){alert('Still Love Film Sync\\n\\nPlease navigate to a watchlist or list page.\\n\\nExample: letterboxd.com/yourusername/watchlist/');return;}function extractFilms(){var films=[];var seen={};var elements=document.querySelectorAll('[data-film-name],[data-item-name]');if(elements.length===0){elements=document.querySelectorAll('.poster-container');}elements.forEach(function(el){var title,year;var filmName=el.getAttribute('data-film-name');var releaseYear=el.getAttribute('data-film-release-year');if(filmName&&releaseYear){title=filmName;year=releaseYear;}else{var itemName=el.getAttribute('data-item-name');if(itemName){var match=itemName.match(/^(.+)\\s+\\((\\d{4})\\)$/);if(match){title=match[1].trim();year=match[2];}}}if(!title){var img=el.querySelector('img[alt]');if(img){var altText=img.getAttribute('alt');var altMatch=altText.match(/^(.+)\\s+\\((\\d{4})\\)$/);if(altMatch){title=altMatch[1].trim();year=altMatch[2];}}}if(title&&year){var key=title.toLowerCase()+'|'+year;if(!seen[key]){seen[key]=true;films.push({title:title,year:year});}}});return films;}var films=extractFilms();if(films.length===0){alert('Still Love Film Sync\\n\\nNo films found on this page.\\n\\nMake sure you are on a Letterboxd watchlist page with films.');return;}var filmData=encodeURIComponent(JSON.stringify(films));var watchlistUrl=encodeURIComponent(window.location.href);var syncUrl=SYNC_PAGE+'?films='+filmData+'&url='+watchlistUrl;if(syncUrl.length>8000){try{var popup=window.open(SYNC_PAGE+'?pending=true&url='+watchlistUrl,'slf_sync','width=500,height=600,scrollbars=yes');if(popup){var checkReady=setInterval(function(){try{popup.postMessage({type:'SLF_SYNC_FILMS',films:films},API_BASE);}catch(e){}},500);setTimeout(function(){clearInterval(checkReady);},30000);}}catch(e){alert('Still Love Film Sync\\n\\nFound '+films.length+' films but the data is too large to sync via bookmarklet.\\n\\nPlease use the CSV import feature instead.');}}else{window.open(syncUrl,'slf_sync','width=500,height=600,scrollbars=yes');}})();`;

// Export for testing
export { BOOKMARKLET_CODE };

export default function BookmarkletPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const bookmarkletRef = useRef<HTMLAnchorElement>(null);

  // Check auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/login');
        return;
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  // Set the bookmarklet href directly on the DOM element to bypass React's javascript: URL sanitization
  // React 18+ blocks javascript: URLs in JSX as a security measure, but we need it for the bookmarklet
  useEffect(() => {
    if (bookmarkletRef.current) {
      bookmarkletRef.current.setAttribute('href', BOOKMARKLET_CODE);
    }
  }, [isLoading]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch {
      // Silent fail on sign out error
    }
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(BOOKMARKLET_CODE);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = BOOKMARKLET_CODE;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <svg
            className="animate-spin h-8 w-8 text-primary mx-auto mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p className="text-dark-500 font-body">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-3 sm:py-4">
          <nav className="flex items-center justify-between">
            <Link
              to="/dashboard"
              className="text-xl sm:text-2xl font-display font-bold text-primary"
            >
              Still Love Film
            </Link>
            <button
              onClick={handleSignOut}
              className="text-dark-500 active:text-primary sm:hover:text-primary transition-colors font-body text-sm sm:text-base min-h-[44px] px-2 touch-manipulation"
            >
              Sign out
            </button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 sm:py-8">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-dark mb-2">
            Sync Your Letterboxd Watchlist
          </h1>
          <p className="text-dark-500 font-body mb-6 sm:mb-8">
            Use this bookmarklet to quickly sync films from your Letterboxd
            watchlist.
          </p>

          {/* Bookmarklet Installation */}
          <section className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-display font-bold text-dark mb-4">
              Step 1: Install the Bookmarklet
            </h2>
            <p className="text-dark-500 font-body mb-4 text-sm sm:text-base">
              Drag the button below to your browser's bookmarks bar:
            </p>

            {/* Draggable Bookmarklet Button */}
            {/* Note: href is set via useEffect to bypass React's javascript: URL sanitization */}
            <div className="flex flex-col items-center gap-4 p-6 bg-primary-50 rounded-xl border-2 border-dashed border-primary-200">
              <a
                ref={bookmarkletRef}
                href="#bookmarklet"
                onClick={(e) => {
                  e.preventDefault();
                  alert(
                    "Drag this button to your bookmarks bar to install the bookmarklet.\n\nClicking it here won't work - it needs to be run from your bookmarks!"
                  );
                }}
                draggable="true"
                className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-lg font-medium shadow-lg hover:bg-primary-700 transition-colors cursor-grab active:cursor-grabbing"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z"
                  />
                </svg>
                Sync to SLF
              </a>
              <p className="text-sm text-dark-500 text-center">
                Drag this button to your bookmarks bar
              </p>
            </div>

            {/* Alternative: Copy Code */}
            <div className="mt-6 pt-6 border-t border-dark-100">
              <p className="text-sm text-dark-500 mb-3">
                <strong>Alternative:</strong> If you can't drag, you can
                manually create a bookmark:
              </p>
              <ol className="text-sm text-dark-500 space-y-1 mb-3 list-decimal list-inside">
                <li>Create a new bookmark in your browser</li>
                <li>Name it "Sync to SLF"</li>
                <li>Paste the code below as the URL</li>
              </ol>
              <button
                onClick={handleCopyCode}
                className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary-700 transition-colors"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                {copied ? 'Copied!' : 'Copy bookmarklet code'}
              </button>
            </div>
          </section>

          {/* How to Use */}
          <section className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-display font-bold text-dark mb-4">
              Step 2: Use the Bookmarklet
            </h2>
            <ol className="space-y-4">
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-bold text-sm">
                  1
                </span>
                <div>
                  <p className="font-medium text-dark">
                    Go to your Letterboxd watchlist
                  </p>
                  <p className="text-sm text-dark-500 mt-1">
                    Navigate to{' '}
                    <code className="bg-dark-50 px-1.5 py-0.5 rounded text-xs">
                      letterboxd.com/yourusername/watchlist/
                    </code>
                  </p>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-bold text-sm">
                  2
                </span>
                <div>
                  <p className="font-medium text-dark">Click the bookmarklet</p>
                  <p className="text-sm text-dark-500 mt-1">
                    Click "Sync to SLF" in your bookmarks bar
                  </p>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-bold text-sm">
                  3
                </span>
                <div>
                  <p className="font-medium text-dark">
                    Select your watchlist and sync
                  </p>
                  <p className="text-sm text-dark-500 mt-1">
                    A popup will open where you can select which Still Love Film
                    watchlist to sync to
                  </p>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center">
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </span>
                <div>
                  <p className="font-medium text-dark">Done!</p>
                  <p className="text-sm text-dark-500 mt-1">
                    Your films are now synced and ready for your weekly picks
                  </p>
                </div>
              </li>
            </ol>
          </section>

          {/* Troubleshooting */}
          <section className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-display font-bold text-dark mb-4">
              Troubleshooting
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-dark mb-1">
                  "No films found on this page"
                </h3>
                <p className="text-sm text-dark-500">
                  Make sure you're on a Letterboxd watchlist or list page with
                  visible films. Try scrolling down to load all films first.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-dark mb-1">
                  Bookmarklet doesn't work
                </h3>
                <p className="text-sm text-dark-500">
                  Some browsers block bookmarklets by default. Try using Chrome
                  or Firefox, or use the{' '}
                  <Link to="/settings" className="text-primary hover:underline">
                    CSV import feature
                  </Link>{' '}
                  instead.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-dark mb-1">
                  "Data too large" error
                </h3>
                <p className="text-sm text-dark-500">
                  If you have a very large watchlist, the bookmarklet may hit
                  browser URL limits. Use the{' '}
                  <Link to="/settings" className="text-primary hover:underline">
                    CSV import feature
                  </Link>{' '}
                  for large collections.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-dark mb-1">
                  Popup was blocked
                </h3>
                <p className="text-sm text-dark-500">
                  Your browser may block popups. Look for a notification in your
                  address bar and allow popups for Letterboxd.
                </p>
              </div>
            </div>
          </section>

          {/* Alternative Methods */}
          <section className="bg-primary-50 rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6 border border-primary-100">
            <h2 className="text-lg sm:text-xl font-display font-bold text-dark mb-2">
              Alternative: CSV Import
            </h2>
            <p className="text-dark-500 font-body mb-4 text-sm sm:text-base">
              You can also export your watchlist from Letterboxd and import the
              CSV file directly. This works better for very large watchlists.
            </p>
            <Link
              to="/settings"
              className="inline-flex items-center gap-2 text-primary hover:text-primary-700 font-medium transition-colors"
            >
              Go to Settings
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </Link>
          </section>

          {/* Back Link */}
          <Link
            to="/settings"
            className="inline-flex items-center text-dark-500 active:text-primary sm:hover:text-primary transition-colors font-body min-h-[44px] touch-manipulation"
          >
            <svg
              className="h-5 w-5 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Settings
          </Link>
        </div>
      </main>
    </div>
  );
}
